import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import * as api from "./api";
import { callTime, emailName } from "./calls";
import { sha256Hex } from "./contracts";
import { go } from "./hooks";
import { orderNote, photoProblem, position, shrink, TAG_TEXT, TAGS, takenAt } from "./photos";
import { notSavedText } from "./today";
import { type Saved, saveFailure } from "./save";
import type { Scope } from "./offline/db";
import { localPhotos, photoEntry } from "./offline/local";
import { keepPhoto, type OutboxEntry, STATE_TEXT } from "./offline/outbox";
import { PhotoThumb } from "./offline/PhotoThumb";
import { Skeleton } from "./Skeleton";

interface Pending {
  id: string;
  blob: Blob;
  taken: string;
}

function PhotoInput({ tag, onTag, busy, busyText, input, onFile }: { tag: api.PhotoTag; onTag: (tag: api.PhotoTag) => void; busy: boolean; busyText: string; input: RefObject<HTMLInputElement>; onFile: (file: File | undefined) => void }) {
  return (
    <>
      <label>
        Photo of
        <select data-testid="photo-tag" value={tag} disabled={busy} onChange={(e) => onTag(e.target.value as api.PhotoTag)}>
          {TAGS.map((value) => (
            <option key={value} value={value}>
              {TAG_TEXT[value]}
            </option>
          ))}
        </select>
      </label>
      <label className="button photo-take">
        {busy ? busyText : "Add a photo"}
        <input ref={input} data-testid="photo-file" type="file" accept="image/*" disabled={busy} onChange={(e) => onFile(e.target.files?.[0])} />
      </label>
    </>
  );
}

/** Design 10.8 on a phone: the phone keeps the photo and sends it through the outbox. The stop shows it immediately. */
function PhotoKeep({
  scope,
  accountId,
  stopId,
  routeId,
  what,
  all,
  testId,
  orderHref,
}: {
  scope: Scope;
  accountId: string;
  stopId: string | null;
  routeId: string | null;
  what: string;
  all: readonly OutboxEntry[];
  testId: string;
  orderHref?: (id: string) => string;
}) {
  const [tag, setTag] = useState<api.PhotoTag>(stopId ? "stop_proof" : "condition");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const mine = useMemo(() => localPhotos(all, accountId, stopId), [all, accountId, stopId]);
  const savedState = mine.find((entry) => entry.id === savedId)?.state ?? "saved";
  const choose = async (file: File | undefined) => {
    if (!file) return;
    setSaving(true);
    setSaved(null);
    setSavedId(null);
    try {
      const blob = await shrink(file);
      const problem = photoProblem(blob.type, blob.size);
      if (problem) {
        setSaved({ ok: false, field: true, text: problem });
        return;
      }
      const taken = takenAt(file.lastModified, Date.now());
      const [place, sha256] = await Promise.all([position(), sha256Hex(await blob.arrayBuffer())]);
      const body: api.PhotoEntry = {
        id: crypto.randomUUID(),
        tag,
        content_type: blob.type as api.PhotoType,
        size_bytes: blob.size,
        sha256,
        taken_at: taken,
        route_stop_id: stopId,
        ...(place ? { lat: place.lat, lon: place.lon } : {}),
      };
      await keepPhoto(scope, photoEntry(accountId, body, `${TAG_TEXT[tag]} photo, ${what}`, routeId), blob);
      console.info(`photo ${body.id}: kept on the phone, ${tag}, ${blob.size} bytes, stop ${stopId ?? "none"}`);
      setSavedId(body.id);
      setSaved({ ok: true, text: `${TAG_TEXT[tag].toLowerCase()} photo.` });
    } catch (e) {
      setSaved({ ok: false, field: true, text: e instanceof Error ? e.message : "The photo was not kept on the phone." });
    } finally {
      setSaving(false);
      if (input.current) input.current.value = "";
    }
  };
  return (
    <div className="photo-add" data-testid={testId}>
      <PhotoInput tag={tag} onTag={setTag} busy={saving} busyText="Saving…" input={input} onFile={(file) => void choose(file)} />
      <p role="status" data-testid="photo-status" className={saved && !saved.ok ? "error" : undefined}>
        {saved?.ok ? `${STATE_TEXT[savedState]}: ${saved.text}` : saved?.text}
      </p>
      {mine.length > 0 && (
        <ul className="photo-local" data-testid="photo-local-list">
          {mine.map((entry) => {
            const kind = entry.body.tag as api.PhotoTag;
            return (
              <li key={entry.id} data-testid="photo-local" data-id={entry.id} data-state={entry.state}>
                <PhotoThumb scope={scope} id={entry.id} alt={`${TAG_TEXT[kind]} photo on this phone`} />
                <span className={`sync-state ${entry.state}`}>{STATE_TEXT[entry.state]}</span>
                {entry.reason && entry.state !== "sent" && <span className={entry.state === "failed" ? "error" : "sub"}>{entry.reason}</span>}
                {entry.state === "sent" && kind === "condition" && orderHref && (
                  <a className="button" href={orderHref(entry.id)} onClick={go} data-testid="photo-order">
                    Open a work order
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Design 10.8 and rule M-3: take or choose a photo, make it smaller, then send it to the bucket. With `keep`, the phone keeps it first. */
export function PhotoAdd({
  accountId,
  stopId,
  user,
  auth,
  testId,
  onSaved,
  orderHref,
  keep,
}: {
  accountId: string;
  stopId?: string;
  user: User;
  auth: Auth;
  testId: string;
  onSaved?: (photo: api.Photo) => void;
  orderHref?: (id: string) => string;
  keep?: { scope: Scope; routeId: string | null; what: string; all: readonly OutboxEntry[] };
}) {
  if (keep) return <PhotoKeep {...keep} accountId={accountId} stopId={stopId ?? null} testId={testId} orderHref={orderHref} />;
  return <PhotoSend accountId={accountId} stopId={stopId} user={user} auth={auth} testId={testId} onSaved={onSaved} orderHref={orderHref} />;
}

function PhotoSend({
  accountId,
  stopId,
  user,
  auth,
  testId,
  onSaved,
  orderHref,
}: {
  accountId: string;
  stopId?: string;
  user: User;
  auth: Auth;
  testId: string;
  onSaved?: (photo: api.Photo) => void;
  orderHref?: (id: string) => string;
}) {
  const [tag, setTag] = useState<api.PhotoTag>(stopId ? "stop_proof" : "condition");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [last, setLast] = useState<api.Photo | null>(null);
  const pending = useRef<Pending | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const send = async () => {
    const photo = pending.current;
    if (!photo) return;
    setSaving(true);
    setSaved({ ok: true, text: "Sending the photo…" });
    try {
      const [place, sha256] = await Promise.all([position(), sha256Hex(await photo.blob.arrayBuffer())]);
      const entry: api.PhotoEntry = {
        id: photo.id,
        tag,
        content_type: photo.blob.type as api.PhotoType,
        size_bytes: photo.blob.size,
        sha256,
        taken_at: photo.taken,
        route_stop_id: stopId ?? null,
        ...(place ? { lat: place.lat, lon: place.lon } : {}),
      };
      const ticket = await api.startPhoto(await user.getIdToken(), accountId, entry);
      await api.uploadFile(ticket, photo.blob);
      const stored = await api.photoStored(await user.getIdToken(), ticket.id);
      pending.current = null;
      if (input.current) input.current.value = "";
      setLast(stored.photo);
      setSaved({ ok: true, text: `Saved: ${TAG_TEXT[stored.photo.tag].toLowerCase()} photo.` });
      onSaved?.(stored.photo);
    } catch (e) {
      const failed = saveFailure(e, "The photo");
      setSaved({ ...failed, text: notSavedText(failed.text) });
    } finally {
      setSaving(false);
    }
  };
  const choose = async (file: File | undefined) => {
    setLast(null);
    if (!file) return;
    const blob = await shrink(file);
    const problem = photoProblem(blob.type, blob.size);
    if (problem) {
      pending.current = null;
      setSaved({ ok: false, field: true, text: problem });
      return;
    }
    pending.current = { id: crypto.randomUUID(), blob, taken: takenAt(file.lastModified, Date.now()) };
    await send();
  };
  const retry = pending.current !== null && saved !== null && !saved.ok && !saved.field;
  return (
    <div className="photo-add" data-testid={testId}>
      <PhotoInput tag={tag} onTag={setTag} busy={saving} busyText="Sending…" input={input} onFile={(file) => void choose(file)} />
      <p role="status" data-testid="photo-status" className={saved && !saved.ok ? "error" : undefined}>
        {saved?.text}
        {retry && (
          <>
            {" "}
            <button type="button" data-testid="photo-retry" disabled={saving} onClick={() => void send()}>
              Try again
            </button>
          </>
        )}
        {saved && !saved.ok && saved.session && (
          <>
            {" "}
            <button type="button" onClick={() => signOut(auth)}>
              Sign in again
            </button>
          </>
        )}
        {last && last.tag === "condition" && orderHref && (
          <>
            {" "}
            <a className="button" href={orderHref(last.id)} onClick={go} data-testid="photo-order">
              Open a work order
            </a>
          </>
        )}
      </p>
    </div>
  );
}

type Shown = { photos: api.Photo[]; canAdd: boolean } | "failed" | undefined;

/** The Photos section of the account page: the newest photos, and the camera when the role adds photos. */
export function AccountPhotos({
  account,
  org,
  auth,
  user,
  focus,
  onOrder,
}: {
  account: api.AccountDetail;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  focus: string | null;
  onOrder: ((note: string) => void) | null;
}) {
  const [shown, setShown] = useState<Shown>(undefined);
  const [attempt, setAttempt] = useState(0);
  const focused = useRef(false);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.accountPhotos(token, account.id))
      .then((found) => live && setShown({ photos: found.photos, canAdd: found.can_add }))
      .catch(() => live && setShown("failed"));
    return () => {
      live = false;
    };
  }, [user, account.id, attempt]);
  const list = typeof shown === "object" ? shown.photos : [];
  useEffect(() => {
    if (focused.current || !focus || !onOrder) return;
    const photo = list.find((found) => found.id === focus && found.tag === "condition");
    if (!photo) return;
    focused.current = true;
    onOrder(orderNote(photo.taken_at, org.time_zone));
  }, [list, focus, onOrder, org.time_zone]);
  return (
    <section aria-labelledby="account-photos" data-testid="photo-section" id="photos">
      <h2 id="account-photos">Photos</h2>
      {typeof shown === "object" && shown.canAdd && (
        <PhotoAdd accountId={account.id} user={user} auth={auth} testId="account-photo-add" onSaved={() => setAttempt((n) => n + 1)} />
      )}
      {shown === undefined ? (
        <Skeleton shape="cards" rows={3} />
      ) : shown === "failed" ? (
        <p role="alert" className="error">
          The photos did not load.{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : list.length === 0 ? (
        <p data-testid="photo-none">No photos yet.</p>
      ) : (
        <ul className="photo-grid" data-testid="photo-list">
          {list.map((photo) => (
            <li key={photo.id} data-testid="photo-item" data-tag={photo.tag}>
              {photo.url && (
                <a href={photo.url} target="_blank" rel="noreferrer">
                  <img src={photo.url} alt={`${TAG_TEXT[photo.tag]} photo of ${callTime(photo.taken_at, org.time_zone)}`} loading="lazy" />
                </a>
              )}
              <span className="sub">
                {TAG_TEXT[photo.tag]} · {callTime(photo.taken_at, org.time_zone)}
                {photo.uploaded_email ? ` · ${emailName(photo.uploaded_email)}` : ""}
              </span>
              {photo.tag === "condition" && onOrder && (
                <button type="button" data-testid="photo-order" onClick={() => onOrder(orderNote(photo.taken_at, org.time_zone))}>
                  Open a work order
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
